import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocListItem,
  DocRun,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { fillFrame, joinSeries, tidy } from "@/lib/tool-kit/essay/sentence";
import { countWords, splitItems } from "@/lib/tool-kit/essay-templates/text";
import {
  isFilled,
  itemOrPrompt,
  lettered,
} from "@/lib/tool-kit/lit-essays/common";

/**
 * An AP History document-based question (DBQ) outline: contextualization, thesis, the
 * documents grouped into body paragraphs with sourcing, evidence beyond the documents,
 * and a complexity plan.
 *
 * The DBQ rubric is shared by AP U.S. History, AP World History: Modern and AP European
 * History and scores seven points (College Board scoring guidelines, 2025):
 *
 * | Row | Point(s) | Earned by |
 * | --- | --- | --- |
 * | A Thesis/Claim | 1 | A historically defensible claim that establishes a line of reasoning |
 * | B Contextualization | 1 | Describing a broader context — more than a phrase |
 * | C Evidence from the documents | 2 | Content of 3 documents (1); supporting an argument with 4 (2) |
 * | C Evidence beyond the documents | 1 | One specific, different piece of evidence, described |
 * | D Sourcing | 1 | For 2 documents, explaining how or why POV, purpose, situation or audience matters |
 * | D Complexity | 1 | Complex understanding as part of the argument |
 *
 * The thresholds were lowered in fall 2023 (earlier: six documents for full evidence
 * credit and three sourced). The checks below follow the current thresholds and are
 * deliberately only a planning aid: "describe in your own words" and "explain how or
 * why" are judgements a human reader makes, so the length tests here are rough proxies.
 */

export type SourcingFeature = "pov" | "purpose" | "situation" | "audience";

export const SOURCING_FEATURES: readonly SourcingFeature[] = [
  "pov",
  "purpose",
  "situation",
  "audience",
];

export type ComplexityMove =
  | ""
  | "perspectives"
  | "multiple"
  | "both"
  | "connections"
  | "sevenDocuments"
  | "sourcingFour"
  | "differentViews";

export const COMPLEXITY_MOVES: readonly Exclude<ComplexityMove, "">[] = [
  "perspectives",
  "multiple",
  "both",
  "connections",
  "sevenDocuments",
  "sourcingFour",
  "differentViews",
];

export interface DbqDocument {
  /** 1–7: the document's number in the exam booklet. */
  id: number;
  /** The body-paragraph group this document supports; 0 for none yet. */
  group: number;
  /** What the document says, in the student's words. */
  summary: string;
  sourcing: SourcingFeature | "";
  /** How or why that feature matters to the argument. */
  sourcingNote: string;
}

export interface DbqGroup {
  id: number;
  /** The group's analytic category: "Relief and security". */
  name: string;
  /** The body paragraph's topic sentence. */
  claim: string;
}

export interface DbqInput {
  prompt: string;
  context: string;
  thesis: string;
  groups: readonly DbqGroup[];
  documents: readonly DbqDocument[];
  /** Evidence beyond the documents, one piece per line, each with its elaboration. */
  outside: string;
  complexityMove: ComplexityMove;
  complexityNote: string;
}

export const DOCUMENT_COUNT = 7;
export const MIN_GROUPS = 2;
export const MAX_GROUPS = 3;

/** Fewer words than this is "a phrase or reference", which the rubric does not credit. */
export const MIN_ELABORATION_WORDS = 6;
const MIN_THESIS_WORDS = 8;

export function blankDocuments(): DbqDocument[] {
  return Array.from({ length: DOCUMENT_COUNT }, (_, index) => ({
    id: index + 1,
    group: 0,
    summary: "",
    sourcing: "",
    sourcingNote: "",
  }));
}

/* ------------------------------------------------------------------ checks */

export interface DbqChecks {
  thesis: boolean;
  contextualization: boolean;
  /** Documents described in the student's words. */
  documentsDescribed: number;
  /** Documents described and placed in a paragraph — used in the argument. */
  documentsUsed: number;
  /** Documents with a sourcing feature and an explanation. */
  documentsSourced: number;
  /** Pieces of evidence beyond the documents that run longer than a phrase. */
  outsideEvidence: number;
  evidenceOne: boolean;
  evidenceTwo: boolean;
  beyond: boolean;
  sourcing: boolean;
  complexity: boolean;
  /** Points the outline is set up to earn, 0–7. */
  planned: number;
}

const NON_WORD = /[^\p{L}\p{N}]+/gu;

function normalized(text: string): string {
  return tidy(text).toLowerCase().replace(NON_WORD, " ").trim();
}

/** A thesis long enough to make a claim, and not a copy of the prompt. */
function thesisReady(input: DbqInput): boolean {
  const thesis = normalized(input.thesis);
  return (
    countWords(thesis) >= MIN_THESIS_WORDS &&
    thesis !== normalized(input.prompt)
  );
}

function explained(text: string): boolean {
  return countWords(text) >= MIN_ELABORATION_WORDS;
}

/** Planning checks against the seven rubric points. */
export function dbqChecks(input: DbqInput): DbqChecks {
  const described = input.documents.filter((doc) => isFilled(doc.summary));
  const used = described.filter((doc) => doc.group > 0);
  const sourced = input.documents.filter(
    (doc) => doc.sourcing !== "" && explained(doc.sourcingNote)
  );
  const outside = splitItems(input.outside).filter(explained);
  const checks = {
    thesis: thesisReady(input),
    contextualization: explained(input.context),
    documentsDescribed: described.length,
    documentsUsed: used.length,
    documentsSourced: sourced.length,
    outsideEvidence: outside.length,
    evidenceOne: described.length >= 3,
    evidenceTwo: used.length >= 4,
    beyond: outside.length >= 1,
    sourcing: sourced.length >= 2,
    complexity: input.complexityMove !== "" && explained(input.complexityNote),
  };
  const points = [
    checks.thesis,
    checks.contextualization,
    checks.evidenceOne,
    checks.evidenceTwo,
    checks.beyond,
    checks.sourcing,
    checks.complexity,
  ].filter(Boolean).length;
  return { ...checks, planned: points };
}

/* ------------------------------------------------------------------ thesis frames */

export const DBQ_THESIS_FRAMES = [
  "Overall, ___ [answer the prompt], because of developments in {categories}.",
  "Although ___ [a counterpoint], ___ [your claim], as seen in {categories}.",
  "From ___ to ___, ___ [your claim], because ___ and ___ [your reasons].",
] as const;

/** The group names as analytic categories, with stand-ins for the unnamed. */
export function categoryList(
  groups: readonly DbqGroup[],
  and: string,
  stand: (n: number) => string
): string {
  const names = groups.map((group, index) =>
    tidy(group.name) === "" ? stand(index + 1) : tidy(group.name)
  );
  return joinSeries(names, and);
}

/** The thesis frames with the paragraph groups worked in as analytic categories. */
export function buildDbqThesisFrames(
  groups: readonly DbqGroup[],
  and: string,
  stand: (n: number) => string,
  frames: readonly string[] = DBQ_THESIS_FRAMES
): string[] {
  const categories = categoryList(groups, and, stand);
  return frames
    .map((frame) => fillFrame(frame, { categories }))
    .filter((text): text is string => text !== null);
}

/* ------------------------------------------------------------------ document */

export interface DbqLabels {
  docTitle: string;
  subtitle: string;
  headings: {
    prompt: string;
    introduction: string;
    body: string;
    unplaced: string;
    outside: string;
    complexity: string;
    rubric: string;
  };
  items: {
    context: string;
    thesis: string;
    claim: string;
    documents: string;
    link: string;
    move: string;
  };
  documentLabel: string;
  sourcingLabels: Readonly<Record<SourcingFeature, string>>;
  sourcingLine: string;
  complexityMoves: Readonly<Record<Exclude<ComplexityMove, "">, string>>;
  prompts: {
    prompt: string;
    context: string;
    thesis: string;
    claim: string;
    noDocuments: string;
    describe: string;
    explain: string;
    outside: string;
    complexity: string;
    link: string;
    unplaced: string;
  };
  guidance: {
    context: string;
    outside: string;
    unplaced: string;
  };
  rubricNote: string;
  plannedLine: string;
  rubric: {
    thesis: string;
    contextualization: string;
    evidenceOne: string;
    evidenceTwo: string;
    beyond: string;
    sourcing: string;
    complexity: string;
  };
  and: string;
  categoryStand: string;
}

function documentName(doc: DbqDocument, labels: DbqLabels): string {
  return t(labels.documentLabel, { n: doc.id });
}

function documentItem(doc: DbqDocument, labels: DbqLabels): DocListItem {
  const name = documentName(doc, labels);
  const runs: DocRun[] = [{ text: name, bold: true }];
  const children: DocListItem[] = [];
  if (doc.sourcing !== "") {
    const feature = labels.sourcingLabels[doc.sourcing];
    children.push(
      explained(doc.sourcingNote)
        ? {
            text: t(labels.sourcingLine, {
              feature,
              note: tidy(doc.sourcingNote),
            }),
          }
        : { text: t(labels.prompts.explain, { feature }), prompt: true }
    );
  }
  const item: DocListItem = isFilled(doc.summary)
    ? { text: [...runs, { text: ` — ${tidy(doc.summary)}` }] }
    : { text: t(labels.prompts.describe, { document: name }), prompt: true };
  return children.length > 0
    ? { ...item, children: { style: "bullet", items: children } }
    : item;
}

function bodySection(
  group: DbqGroup,
  index: number,
  input: DbqInput,
  labels: DbqLabels
): DocSection {
  const docs = input.documents.filter((doc) => doc.group === group.id);
  const docItems: DocListItem[] =
    docs.length === 0
      ? [{ text: labels.prompts.noDocuments, prompt: true }]
      : docs.map((doc) => documentItem(doc, labels));
  const name = tidy(group.name);
  return {
    heading: t(labels.headings.body, {
      n: index + 1,
      category: name === "" ? t(labels.categoryStand, { n: index + 1 }) : name,
    }),
    blocks: [
      lettered([
        itemOrPrompt(
          labels.items.claim,
          tidy(group.claim),
          labels.prompts.claim
        ),
        {
          text: [{ text: `${labels.items.documents}:`, bold: true }],
          children: { style: "bullet", items: docItems },
        },
        { text: labels.prompts.link, prompt: true },
      ]),
    ],
  };
}

function unplacedSection(
  input: DbqInput,
  labels: DbqLabels
): DocSection | null {
  const unplaced = input.documents.filter(
    (doc) => doc.group === 0 && isFilled(doc.summary)
  );
  if (unplaced.length === 0) {
    return null;
  }
  return {
    heading: labels.headings.unplaced,
    blocks: [
      { kind: "guidance", text: labels.guidance.unplaced },
      {
        kind: "list",
        list: {
          style: "bullet",
          items: unplaced.map((doc) => documentItem(doc, labels)),
        },
      },
    ],
  };
}

function outsideSection(input: DbqInput, labels: DbqLabels): DocSection {
  const lines = splitItems(input.outside);
  const blocks: DocBlock[] = [
    { kind: "guidance", text: labels.guidance.outside },
  ];
  blocks.push(
    lines.length === 0
      ? { kind: "prompt", text: labels.prompts.outside }
      : {
          kind: "list",
          list: {
            style: "bullet",
            items: lines.map((line) => ({ text: line })),
          },
        }
  );
  return { heading: labels.headings.outside, blocks };
}

function complexitySection(input: DbqInput, labels: DbqLabels): DocSection {
  const blocks: DocBlock[] = [];
  if (input.complexityMove !== "") {
    blocks.push({
      kind: "fields",
      fields: [
        {
          label: labels.items.move,
          value: labels.complexityMoves[input.complexityMove],
        },
      ],
    });
  }
  blocks.push(
    isFilled(input.complexityNote)
      ? { kind: "paragraph", text: tidy(input.complexityNote) }
      : { kind: "prompt", text: labels.prompts.complexity }
  );
  return { heading: labels.headings.complexity, blocks };
}

function rubricSection(input: DbqInput, labels: DbqLabels): DocSection {
  const checks = dbqChecks(input);
  return {
    heading: labels.headings.rubric,
    blocks: [
      { kind: "guidance", text: labels.rubricNote },
      {
        kind: "checklist",
        items: [
          { text: labels.rubric.thesis, checked: checks.thesis },
          {
            text: labels.rubric.contextualization,
            checked: checks.contextualization,
          },
          { text: labels.rubric.evidenceOne, checked: checks.evidenceOne },
          { text: labels.rubric.evidenceTwo, checked: checks.evidenceTwo },
          { text: labels.rubric.beyond, checked: checks.beyond },
          { text: labels.rubric.sourcing, checked: checks.sourcing },
          { text: labels.rubric.complexity, checked: checks.complexity },
        ],
      },
      {
        kind: "paragraph",
        text: t(labels.plannedLine, { planned: checks.planned }),
      },
    ],
  };
}

/** The DBQ plan as a document. */
export function buildDbqOutline(
  input: DbqInput,
  labels: DbqLabels
): DocumentModel {
  const sections: DocSection[] = [
    {
      heading: labels.headings.prompt,
      blocks: [
        isFilled(input.prompt)
          ? { kind: "paragraph", text: tidy(input.prompt) }
          : { kind: "prompt", text: labels.prompts.prompt },
      ],
    },
    {
      heading: labels.headings.introduction,
      blocks: [
        { kind: "guidance", text: labels.guidance.context },
        lettered([
          itemOrPrompt(
            labels.items.context,
            tidy(input.context),
            labels.prompts.context
          ),
          itemOrPrompt(
            labels.items.thesis,
            tidy(input.thesis),
            labels.prompts.thesis
          ),
        ]),
      ],
    },
    ...input.groups.map((group, index) =>
      bodySection(group, index, input, labels)
    ),
  ];
  const unplaced = unplacedSection(input, labels);
  if (unplaced) {
    sections.push(unplaced);
  }
  sections.push(
    outsideSection(input, labels),
    complexitySection(input, labels),
    rubricSection(input, labels)
  );
  return { title: labels.docTitle, subtitle: labels.subtitle, sections };
}
